'use client';
import React from 'react';
import Link from 'next/link';
import { formatPlanDate, useMyMembershipPlan } from '@/lib/membership/useMyMembershipPlan';
import ConsentText from '@/components/public/ConsentText';
import { offerPriceLabel, useMembershipOffers } from '@/lib/membership/useMembershipOffers';

const badgeBase = 'mt-2 inline-flex max-w-full items-center whitespace-normal rounded-full px-2.5 py-1 text-[11px] font-semibold leading-snug';
const buttonClass =
  'block rounded-lg bg-[#581C32] px-3 py-2 text-center text-xs font-semibold text-white transition-opacity hover:opacity-90';

// Mounted only for Starter members, so membership offers are fetched only where they are shown.
function StarterUpgrades() {
  const { offers } = useMembershipOffers();
  const starterUpgrades = offers
    .filter((o) => (o.route === 'growth' || o.route === 'growth_governance') && o.paymentUrl)
    .map((o) => ({ key: o.route, label: `${o.name} ${offerPriceLabel(o)}`.trim(), href: o.paymentUrl as string }));
  return (
    <>
      {starterUpgrades.map((u) => (
        <a key={u.key} href={u.href} target="_blank" rel="noopener noreferrer" className={buttonClass}>
          {u.label}
        </a>
      ))}
    </>
  );
}

export default function YourPlanCard() {
  const { loading, plan } = useMyMembershipPlan();

  let body: React.ReactNode;

  if (loading) {
    body = <p className="mt-1 text-sm font-semibold text-[#581C32]/70">Loading…</p>;
  } else if (!plan || plan.status === 'not_assigned') {
    body = (
      <>
        <p className="mt-1 text-sm font-semibold text-[#581C32]">Not assigned yet</p>
        <p className="mt-0.5 text-xs text-[#581C32]/75">Not assigned yet. Your membership is assigned by the CBS team.</p>
      </>
    );
  } else {
    const expiry = formatPlanDate(plan.expiryDate);
    const start = formatPlanDate(plan.startDate);
    const isStarter = plan.route === 'starter';
    const buildersEnding =
      plan.route === 'builders' && plan.daysRemaining !== null && plan.daysRemaining <= 30 && plan.status !== 'expired';

    let badge: React.ReactNode = null;
    if (plan.status === 'active') {
      badge = <span className={`${badgeBase} bg-green-100 text-green-800`}>Active</span>;
    } else if (plan.status === 'paid_in_full') {
      badge = <span className={`${badgeBase} bg-green-100 text-green-800`}>Paid in full</span>;
    } else if (plan.status === 'ending') {
      badge = <span className={`${badgeBase} bg-[#C6A667] text-[#581C32]`}>Ends {expiry}</span>;
    } else if (plan.status === 'payment_overdue') {
      badge = <span className={`${badgeBase} bg-amber-100 text-amber-900`}>Payment due: please check your payment details</span>;
    } else if (plan.status === 'expired') {
      badge = <span className={`${badgeBase} bg-gray-200 text-gray-700`}>Expired</span>;
    }

    body = (
      <>
        <p className="mt-1 text-xl font-bold leading-tight text-[#581C32]">{plan.planName ?? 'Your membership'}</p>
        {plan.planDescription && <p className="mt-1 text-xs text-[#581C32]/80">{plan.planDescription}</p>}
        {(start || expiry) && (
          <div className="mt-2 space-y-0.5 text-xs text-[#581C32]/75">
            {start && <p>Started {start}</p>}
            {expiry && <p>Access until {expiry}</p>}
          </div>
        )}
        {badge}
        {plan.status === 'expired' && (
          <p className="mt-2 text-xs text-[#581C32]/80">Your access has ended. Renew to continue your learning.</p>
        )}

        {plan.isStaff && <p className="mt-2 text-xs font-semibold text-[#581C32]">Superuser (full access)</p>}

        {!plan.isStaff && isStarter && (
          <div className="mt-3 space-y-2">
            <p className="text-xs font-semibold text-[#581C32]">Upgrade to continue to Course 2</p>
            <StarterUpgrades />
            <ConsentText className="text-[#581C32]" />
            <p className="text-[11px] text-[#581C32]/70">Please pay with the same email address you log in with.</p>
          </div>
        )}

        {!plan.isStaff && buildersEnding && (
          <div className="mt-3">
            <p className="text-xs text-[#581C32]/85">
              Your Builders Membership ends soon. Speak to Linda about continuing.
            </p>
            <Link href="/contact" className={`${buttonClass} mt-2`}>
              Speak to Linda
            </Link>
          </div>
        )}
      </>
    );
  }

  return (
    <div className="rounded-xl border border-[#C6A667] bg-[#F8F3E9] p-4">
      <p className="text-[10px] font-bold uppercase tracking-[0.12em] text-[#581C32]">Your plan</p>
      {body}
    </div>
  );
}
